import { register } from './index';
import { getInstanceDefinition } from '../cardLookup';
import {
  discardFromHand, destroyFromHand, applyInstanceOverride, battleTargetableHeroes, queueHandChoice, grantMagicAttack,
  estimateHeroAttackValue, eliminatePlayer,
} from '../effectHelpers';

const HERO_CLASSES = ['Fighter', 'Wizard', 'Cleric', 'Thief', 'Ranger'];

// NOTE: this used to check `choices?.instanceId`, which was dead code -
// resolveCombat.js's resolveTimedHandlers hardcodes `choices: {}` for every
// Battle/Aftermath handler call, so nothing supplied on CHOOSE_MONSTER's
// payload could ever reach here. Converted to the same pending-queue
// pattern as monsters.js's Raid/Battle/Aftermath handlers.
register('bearer-stramst', {
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = battleTargetableHeroes(state, player);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Stramst: destroy a hero.' });
    },
  }],
  // Global (hall-refill pinning) is handled directly in dungeon/hall.js.
});

register('bearer-belac', {
  battle: [{
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const heroes = battleTargetableHeroes(state, player);
      if (!heroes.length) return state;
      const ranked = heroes
        .map((id) => ({ id, attack: estimateHeroAttackValue(state, player, id) }))
        .sort((a, b) => b.attack - a.attack);
      const best = ranked[0];
      let nextState = discardFromHand(state, playerId, best.id);
      const belacDef = getInstanceDefinition(nextState, instanceId);
      nextState = applyInstanceOverride(nextState, instanceId, { health: (belacDef.health || 0) + best.attack });
      return nextState;
    },
  }],
  // Global ("resolve all other monsters' Battle effects twice") is handled
  // directly in combat/resolveCombat.js.
});

// ==========================================================================
// Caverns of Bane
// ==========================================================================

register('bearer-goron-singlmind', {
  // "Suffer Health -2 for each class present." "Class" = the 5 printed hero
  // classes (Fighter/Wizard/Cleric/Thief/Ranger), counted by DISTINCT class
  // present in hand, not per hero card - matches village-force-blast's own
  // "hero class present" wording convention (not yet in scope, but the same
  // source-data phrase).
  battle: [{
    resolve: (state, { playerId, instanceId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const classCount = HERO_CLASSES.filter((cls) => player.hand.some((id) => getInstanceDefinition(state, id).traits.includes(cls))).length;
      if (!classCount) return state;
      const def = getInstanceDefinition(state, instanceId);
      return applyInstanceOverride(state, instanceId, { health: Math.max(0, (def.health || 0) - 2 * classCount) });
    },
  }],
});

// Xobmokt, Ichor King: "Global: Whenever a player discards a card before the
// end of the turn, destroy that card." NOT YET IMPLEMENTED - would need a
// hook at every discard call-site in the game (village abilities, monster
// Battle/Aftermath effects, Curses, everything that calls discardFromHand),
// gated on whether Xobmokt is currently in the hall. Nothing in ToR/CoB so
// far needs a hall-presence hook this broad; every existing hall-presence
// effect (Ambusher checks, Darkness, Trophy) reads state once at a fixed
// point, not "at every future discard this turn." Flagged on
// docs/expansion-audit.md's review list.

register('bearer-mowtil-djinni-lich', {
  // "Magic Attack required" (combat/attackMath.js MONSTER_IMMUNITIES
  // voidAttackType) and "Add Health +1 for each card present" (a Health
  // bonus scaled by hand size, special-cased directly in
  // combat/resolveCombat.js since alwaysActive health scaling has no home
  // in the standard registry - see SPECIAL_CASE_ALWAYS_ACTIVE) are both
  // handled outside this registration; see effectRegistryCoverage.test.js.
  aftermath: [{
    // "If this card is not defeated, destroy a card of 1 VP or higher."
    resolve: (state, { playerId, won }) => {
      if (won) return state;
      const player = state.players.find((p) => p.id === playerId);
      const candidateInstanceIds = player.hand.filter((id) => (getInstanceDefinition(state, id).points || 0) >= 1);
      return queueHandChoice(state, { playerId, verb: 'destroy', candidateInstanceIds, reason: 'Mowtil, Djinni Lich: destroy a card of 1 VP or higher.' });
    },
  }],
});

// ==========================================================================
// Root of Corruption
// ==========================================================================

// King Caelan has no printed Battle/Aftermath/Trophy/Global text at all -
// "You cannot select this monster to fight if you have used a Dungeon
// ability on a hero or villager" is a fight-eligibility restriction (see
// dungeon/fightEligibility.js's FIGHT_ELIGIBILITY), and its Guardian trait
// (never retreats on a loss) is already covered generically by
// combat/resolveCombat.js's isGuardian branch. No register() call needed.

// ==========================================================================
// Numenera
// ==========================================================================

register('bearer-phylethis', {
  // "Battle: Discard cards equal to the highest level of monster in the hall."
  battle: [{
    resolve: (state, { playerId }) => {
      const levels = state.dungeonHall
        .filter(Boolean)
        .map((id) => getInstanceDefinition(state, id))
        .filter((def) => def.type === 'monster' || def.type === 'bearer')
        .map((def) => def.level || 0);
      const count = levels.length ? Math.max(...levels) : 0;
      if (!count) return state;
      let nextState = state;
      for (let i = 0; i < count; i += 1) {
        const player = nextState.players.find((p) => p.id === playerId);
        nextState = queueHandChoice(nextState, { playerId, verb: 'discard', candidateInstanceIds: player.hand, reason: 'Phylethis: discard cards equal to the highest level of monster in the hall.' });
      }
      return nextState;
    },
  }],
});

// Sarrak: "Ignore heroes of level 2 and lower." -
// combat/attackMath.js MONSTER_IMMUNITIES (maxHeroLevel). No other printed text.

register('bearer-convergence-master', {
  // "Add Health +1 for each card present" is special-cased in
  // combat/resolveCombat.js (same pattern as Mowtil Djinni Lich's own
  // Health-scaling special case - see SPECIAL_CASE_ALWAYS_ACTIVE).
  // "This card gains the Battle ability on each card present" is NOT YET
  // IMPLEMENTED - unclear what a HAND card's own "Battle ability" would
  // even mean (Battle is normally a monster-only timing keyword; no
  // rulebook is available for this crossover set to confirm the intended
  // reading), so this is flagged rather than guessed at. See
  // docs/expansion-audit.md's review list.
});

// ==========================================================================
// Starter set
// ==========================================================================

register('bearer-wraithlyn-dragon-general', {
  // "Discard 1 weapon, 1 spell, and 1 item (do as much of this as you
  // can)." Three independent single-card discard picks, each a no-op if
  // that trait pool is empty (queueHandChoice already no-ops on an empty
  // candidate list) - together this already IS "do as much as you can."
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      let nextState = queueHandChoice(state, {
        playerId, verb: 'discard', candidateInstanceIds: player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Weapon')), reason: 'Wraithlyn: discard 1 weapon.',
      });
      nextState = queueHandChoice(nextState, {
        playerId, verb: 'discard', candidateInstanceIds: player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Spell')), reason: 'Wraithlyn: discard 1 spell.',
      });
      nextState = queueHandChoice(nextState, {
        playerId, verb: 'discard', candidateInstanceIds: player.hand.filter((id) => getInstanceDefinition(state, id).traits.includes('Item')), reason: 'Wraithlyn: discard 1 item.',
      });
      return nextState;
    },
  }],
});

// ==========================================================================
// Into the Abyss
// ==========================================================================

register('bearer-heart-of-doom', {
  // "Battle: Add Magic Attack +6 to each Thunderstone Shard." A per-count
  // pooled grant, same "count matching present cards, multiply by N"
  // convention as village-village-headman/village-augmentation-amulet's own
  // "for each X present" lines - keyed off the specific card id (Thunderstone
  // Shard is a basic, not a trait-distinguishable card the way Villager/
  // human/elf/dwarf are).
  battle: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const shardCount = player.hand.filter((id) => getInstanceDefinition(state, id).id === 'basic-thunderstone-shard').length;
      return shardCount ? grantMagicAttack(state, playerId, 6 * shardCount) : state;
    },
  }],
  // "Global: When a player enters the dungeon, destroy all monsters
  // present." - effectRegistry/monsters.js's DUNGEON_VISIT_HALL_EFFECTS,
  // wired from actions/index.js's ENTER_DUNGEON case (no register() entry
  // needed here, same convention as Desmodic/Magma Wyrm's own
  // village-visit equivalent).
  //
  // "Aftermath: If this card is not defeated, the active player loses." -
  // Matt's own ruling: true elimination (removed from turn rotation and
  // winner contention; the game continues for everyone else UNLESS this
  // leaves exactly one player remaining, in which case it ends immediately
  // and they win outright). See effectHelpers.js's eliminatePlayer.
  aftermath: [{
    resolve: (state, { playerId, won }) => (won ? state : eliminatePlayer(state, playerId)),
  }],
});

register('bearer-oblivion-deathkeeper', {
  // "Global: Discard 1 random card when you enter the dungeon." -
  // effectRegistry/monsters.js's DUNGEON_VISIT_HALL_EFFECTS, same as Heart
  // of Doom's own Global above (no register() entry needed here either).
});

// ==========================================================================
// Worlds Collide
// ==========================================================================

register('bearer-the-last-doomknight', {
  // "Global: Suffer Light -1. Each hero suffers Strength -1. Other monsters
  // add Health +1." All 3 sub-effects special-cased directly in
  // combat/resolveCombat.js (anyLastDoomknightInHall, effectHelpers.js) -
  // no register() entry needed for the Global itself, same convention as
  // Stramst/Belac's own Globals above.
});

register('bearer-void-apocalypse', {
  // "alwaysActive: Choose 1 copy of each hero present (by card title).
  // Ignore all other heroes." - combat/attackMath.js's contributesTraits
  // (deterministic first-copy-wins; every copy of a title has identical
  // stats, so there's no real choice to offer a UI for).
  //
  // "Aftermath: Destroy all cards that have more than 1 copy present."
  // Reads as "for every card title with 2+ copies in hand, destroy every
  // copy of it" (not "destroy down to 1 remaining") - the more literal
  // reading of "destroy ALL cards that have more than 1 copy," but genuinely
  // ambiguous without a rulebook; flagged for review (see
  // docs/expansion-audit.md).
  aftermath: [{
    resolve: (state, { playerId }) => {
      const player = state.players.find((p) => p.id === playerId);
      const counts = {};
      for (const id of player.hand) {
        const defId = getInstanceDefinition(state, id).id;
        counts[defId] = (counts[defId] || 0) + 1;
      }
      const toDestroy = player.hand.filter((id) => counts[getInstanceDefinition(state, id).id] > 1);
      let nextState = state;
      for (const id of toDestroy) {
        const current = nextState.players.find((p) => p.id === playerId);
        if (!current.hand.includes(id)) continue; // an earlier destroy in this same loop already removed it
        nextState = destroyFromHand(nextState, playerId, id, { battleEffect: true });
      }
      return nextState;
    },
  }],
});
